<template>
  <div class="traffic-light-section">
    <div class="section-header">
      <h5>
        <i class="bi" :class="categoryIcon"></i>
        {{ categoryLabel }}
      </h5>
      <span class="completion-badge" :class="completionClass">
        {{ completedCount }}/{{ totalCount }}
      </span>
    </div>

    <div class="skills-list">
      <div
        v-for="skill in skills"
        :key="skill.key"
        class="skill-row"
        :class="{ readonly: readonly, rated: hasRating(skill.key) }"
      >
        <div class="skill-label">
          {{ skill.label }}
        </div>
        <div class="rag-options">
          <label
            v-for="option in ragOptions"
            :key="option.value"
            class="rag-option"
            :class="[
              option.value,
              {
                selected: isSelected(skill.key, option.value),
                disabled: readonly,
              },
            ]"
          >
            <input
              type="radio"
              :name="`${category}_${skill.key}`"
              :value="option.value"
              :checked="isSelected(skill.key, option.value)"
              :disabled="readonly"
              @change="updateRating(skill.key, option.value)"
            />
            <span class="rag-indicator" :title="option.description">
              {{ option.label.charAt(0) }}
            </span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getSkillsForPlayerType,
  CATEGORIES,
  RAG_OPTIONS,
} from "@/config/traffic-light-review-config";

export default {
  name: "TrafficLightSection",
  props: {
    // Category: 'technical', 'tactical', 'physical', 'mental'
    category: {
      type: String,
      required: true,
      validator: (value) =>
        ["technical", "tactical", "physical", "mental"].includes(value),
    },
    // Player type: 'outfield' or 'goalkeeper'
    playerType: {
      type: String,
      default: "outfield",
      validator: (value) => ["outfield", "goalkeeper"].includes(value),
    },
    // v-model binding for the category ratings object
    modelValue: {
      type: Object,
      default: () => ({}),
    },
    // Whether the section is read-only
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  computed: {
    skills() {
      const allSkills = getSkillsForPlayerType(this.playerType);
      return allSkills[this.category] || [];
    },
    categoryLabel() {
      return CATEGORIES[this.category]?.label || this.category;
    },
    categoryIcon() {
      return CATEGORIES[this.category]?.icon || "bi-list";
    },
    ragOptions() {
      return RAG_OPTIONS;
    },
    totalCount() {
      return this.skills.length;
    },
    completedCount() {
      return this.skills.filter((skill) => this.hasRating(skill.key)).length;
    },
    completionClass() {
      if (this.completedCount === 0) return "none";
      if (this.completedCount === this.totalCount) return "complete";
      return "partial";
    },
  },
  methods: {
    hasRating(skillKey) {
      return !!this.modelValue?.[skillKey];
    },
    isSelected(skillKey, value) {
      return this.modelValue?.[skillKey] === value;
    },
    updateRating(skillKey, value) {
      if (this.readonly) return;

      const updatedValue = {
        ...this.modelValue,
        [skillKey]: value,
      };
      this.$emit("update:modelValue", updatedValue);
    },
  },
};
</script>

<style scoped>
.traffic-light-section {
  background: #fff;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  border-left: 4px solid var(--category-color, #005596);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e9ecef;
}

.section-header h5 {
  margin: 0;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-header h5 i {
  color: var(--category-color, #005596);
}

.completion-badge {
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.completion-badge.none {
  background: #f8f9fa;
  color: #6c757d;
}

.completion-badge.partial {
  background: #fff3cd;
  color: #856404;
}

.completion-badge.complete {
  background: #d4edda;
  color: #155724;
}

.skills-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.skill-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  transition: background 0.2s ease;
}

.skill-row:hover:not(.readonly) {
  background: #e9ecef;
}

.skill-row.rated {
  background: #f0f7ff;
}

.skill-row.readonly {
  opacity: 0.8;
}

.skill-label {
  flex: 1;
  font-size: 0.9rem;
  color: #333;
  padding-right: 1rem;
}

.rag-options {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.rag-option {
  margin: 0;
  cursor: pointer;
}

.rag-option.disabled {
  cursor: not-allowed;
}

.rag-option input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.rag-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-weight: 700;
  font-size: 0.85rem;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

/* Red option */
.rag-option.red .rag-indicator {
  background: #f8d7da;
  color: #721c24;
  border-color: #f5c6cb;
}

.rag-option.red:hover:not(.disabled) .rag-indicator,
.rag-option.red.selected .rag-indicator {
  background: #dc3545;
  color: #fff;
  border-color: #dc3545;
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.4);
}

/* Amber option */
.rag-option.amber .rag-indicator {
  background: #fff3cd;
  color: #856404;
  border-color: #ffeeba;
}

.rag-option.amber:hover:not(.disabled) .rag-indicator,
.rag-option.amber.selected .rag-indicator {
  background: #ffc107;
  color: #212529;
  border-color: #ffc107;
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.4);
}

/* Green option */
.rag-option.green .rag-indicator {
  background: #d4edda;
  color: #155724;
  border-color: #c3e6cb;
}

.rag-option.green:hover:not(.disabled) .rag-indicator,
.rag-option.green.selected .rag-indicator {
  background: #28a745;
  color: #fff;
  border-color: #28a745;
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.4);
}

/* Category-specific colors */
.traffic-light-section[data-category="technical"] {
  --category-color: #005596;
}

.traffic-light-section[data-category="tactical"] {
  --category-color: #17a2b8;
}

.traffic-light-section[data-category="physical"] {
  --category-color: #fd7e14;
}

.traffic-light-section[data-category="mental"] {
  --category-color: #6f42c1;
}

/* Responsive */
@media (max-width: 576px) {
  .traffic-light-section {
    padding: 0.75rem;
  }

  .section-header h5 {
    font-size: 0.95rem;
  }

  .skill-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.75rem;
  }

  .skill-label {
    font-size: 0.85rem;
    padding-right: 0;
  }

  .rag-options {
    width: 100%;
    justify-content: flex-end;
  }

  .rag-indicator {
    width: 36px;
    height: 36px;
  }
}
</style>
